import React, { useCallback } from "react";
import {
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Stack,
} from "@mui/material";
import { DesignCategory } from "./constants/designCategories";

const toTitleCase = (str: string): string => {
  return str.replace(/_/g, " ").replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
  });
};

interface DesktopDesignCategoryFilterProps {
  selectedCategories: DesignCategory[];
  onCategoryToggle: (category: DesignCategory) => void;
  availableCategories: string[];
}

const DesktopDesignCategoryFilter: React.FC<
  DesktopDesignCategoryFilterProps
> = ({ selectedCategories = [], onCategoryToggle, availableCategories }) => {
  const allCategories = availableCategories as DesignCategory[];
  const allSelected = allCategories.every((cat) =>
    selectedCategories.includes(cat)
  );
  const someSelected = selectedCategories.length > 0 && !allSelected;

  const handleSelectAll = useCallback(() => {
    allCategories.forEach((cat) => {
      if (!selectedCategories.includes(cat)) {
        onCategoryToggle(cat as DesignCategory);
      }
    });
  }, [allCategories, onCategoryToggle, selectedCategories]);

  const handleClearAll = useCallback(() => {
    selectedCategories.forEach((cat) =>
      onCategoryToggle(cat as DesignCategory)
    );
  }, [onCategoryToggle, selectedCategories]);

  return (
    <div className="d-none d-lg-block">
      <h1 className="h5 fw-semibold mb-3">Design Category</h1>
      <div className="mb-3" style={{ paddingRight: "15px" }}>
        <Stack
          direction="row"
          spacing={2}
          sx={{ justifyContent: "flex-start" }}
        >
          <Button
            size="small"
            variant={allSelected ? "outlined" : "contained"}
            onClick={handleSelectAll}
            disabled={allSelected}
            className="text-nowrap"
            sx={{
              "&.Mui-disabled": {
                backgroundColor: "#f5f5f5",
                color: "rgba(0, 0, 0, 0.26)",
                borderColor: "rgba(0, 0, 0, 0.12)",
              },
              "&:hover": {
                backgroundColor: allSelected ? "transparent" : undefined,
              },
            }}
          >
            Select All
          </Button>
          <Button
            size="small"
            variant={selectedCategories.length === 0 ? "outlined" : "contained"}
            onClick={handleClearAll}
            disabled={selectedCategories.length === 0}
            className="text-nowrap"
            sx={{
              "&.Mui-disabled": {
                backgroundColor: "#f5f5f5",
                color: "rgba(0, 0, 0, 0.26)",
                borderColor: "rgba(0, 0, 0, 0.12)",
              },
              "&:hover": {
                backgroundColor:
                  selectedCategories.length === 0 ? "transparent" : undefined,
              },
            }}
          >
            Clear All
          </Button>
        </Stack>
      </div>
      <FormGroup className="mt-2">
        {availableCategories.map((category) => (
          <FormControlLabel
            key={category}
            control={
              <Checkbox
                checked={selectedCategories.includes(category)}
                onChange={() => onCategoryToggle(category)}
                size="small"
                sx={{
                  '&.MuiCheckbox-root': {
                    '&:not(.Mui-checked)': {
                      '& .MuiSvgIcon-root': {
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                        borderRadius: '4px',
                        width: '18px',
                        height: '18px',
                      },
                    },
                    '&.Mui-checked .MuiSvgIcon-root': {
                      border: 'none',
                    },
                  },
                }}
              />
            }
            label={toTitleCase(category)}
            className="text-sm"
          />
        ))}
      </FormGroup>
    </div>
  );
};

export default DesktopDesignCategoryFilter;
